import {IROIWidgetConfig, IActivityConfig} from "../models/roi-widget.props";
import {ACTIVITY_TYPES} from "../constants";

export class ROIConfigService {
    private static readonly STORAGE_KEY = "gluware_roi_widget_config";

    /**
     * Convert UI configuration to backend format
     */
    static convertToBackendFormat(uiConfig: IROIWidgetConfig): IROIWidgetConfig {
        const backendConfig: IROIWidgetConfig = {
            // Widget display settings
            title: uiConfig.title,
            metric: uiConfig.metric,
            visualizationMode: uiConfig.visualizationMode,
            compareTo: uiConfig.compareTo,
            reportFormat: uiConfig.reportFormat,

            // Backend format
            hourlyRate: uiConfig.globalSettings?.averageHourlyRate || 0,
            currencyCode: uiConfig.globalSettings?.currency?.value || "USD",
            activities: {},
        };

        // Convert task configuration to activities format
        if (uiConfig.taskConfiguration) {
            uiConfig.taskConfiguration.forEach((task) => {
                if (task.enabled && task.timePerTask > 0) {
                    backendConfig.activities![task.id] = {
                        hoursPerTask: task.timePerTask,
                        monthlyFrequency: 1, // Default value
                        totalMonthlyHours: task.timePerTask, // Default to same as hoursPerTask
                    };
                }
            });
        }

        return backendConfig;
    }

    /**
     * Convert backend configuration to UI format
     */
    static convertToUIFormat(backendConfig: IROIWidgetConfig): IROIWidgetConfig {
        const uiConfig: IROIWidgetConfig = {
            // Widget display settings
            title: backendConfig.title,
            metric: backendConfig.metric,
            visualizationMode: backendConfig.visualizationMode,
            compareTo: backendConfig.compareTo,
            reportFormat: backendConfig.reportFormat,

            // UI format - handle both backend formats
            globalSettings: {
                averageHourlyRate:
                    backendConfig.hourlyRate ||
                    backendConfig.globalSettings?.averageHourlyRate ||
                    0,
                currency: backendConfig.globalSettings?.currency || {
                    value: backendConfig.currencyCode || "USD",
                    label: this.getCurrencyLabel(backendConfig.currencyCode || "USD"),
                },
            },
            taskConfiguration: [],
        };

        // Convert activities or taskConfiguration to task configuration format
        if (backendConfig.taskConfiguration && Array.isArray(backendConfig.taskConfiguration)) {
            // Backend already has taskConfiguration in UI format
            uiConfig.taskConfiguration = backendConfig.taskConfiguration;
        } else if (backendConfig.activities) {
            // Convert activities to task configuration format
            Object.keys(backendConfig.activities).forEach((activityType) => {
                const activity = backendConfig.activities![activityType];
                uiConfig.taskConfiguration!.push({
                    id: activityType,
                    name: this.getActivityDisplayName(activityType),
                    enabled: true,
                    timePerTask: activity.hoursPerTask,
                    unit: "hours" as const,
                });
            });
        }

        return uiConfig;
    }

    /**
     * Save configuration to localStorage
     */
    static saveConfig(config: IROIWidgetConfig): void {
        try {
            const backendConfig = this.convertToBackendFormat(config);
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(backendConfig));
        } catch (error) {
            console.error("Failed to save ROI configuration:", error);
        }
    }

    /**
     * Load configuration from localStorage
     */
    static loadConfig(): IROIWidgetConfig | null {
        try {
            const stored = localStorage.getItem(this.STORAGE_KEY);
            if (stored) {
                const backendConfig = JSON.parse(stored) as IROIWidgetConfig;
                const uiConfig = this.convertToUIFormat(backendConfig);
                return uiConfig;
            }
        } catch (error) {
            console.error("Failed to load ROI configuration:", error);
        }
        return null;
    }

    /**
     * Clear configuration from localStorage
     */
    static clearConfig(): void {
        try {
            localStorage.removeItem(this.STORAGE_KEY);
        } catch (error) {
            console.error("Failed to clear ROI configuration:", error);
        }
    }

    /**
     * Validate configuration for backend compatibility
     */
    static validateConfig(config: IROIWidgetConfig): {isValid: boolean; errors: string[]} {
        const errors: string[] = [];

        // Check required fields
        if (!config.hourlyRate || config.hourlyRate <= 0) {
            errors.push("Hourly rate must be greater than 0");
        }

        if (!config.currencyCode) {
            errors.push("Currency code is required");
        }

        if (!config.activities || Object.keys(config.activities).length === 0) {
            errors.push("At least one activity must be configured");
        }

        // Validate activities
        if (config.activities) {
            Object.keys(config.activities).forEach((activityType) => {
                const activity = config.activities![activityType];
                if (!activity.hoursPerTask || activity.hoursPerTask <= 0) {
                    errors.push(`Activity ${activityType} must have hours per task greater than 0`);
                }
            });
        }

        return {
            isValid: errors.length === 0,
            errors,
        };
    }

    /**
     * Get display name for activity type
     */
    private static getActivityDisplayName(activityType: string): string {
        const displayNames: {[key: string]: string} = {
            [ACTIVITY_TYPES.CAPTURE]: "Device Discovery",
            [ACTIVITY_TYPES.FEATURE_PROVISIONING]: "Config Deployment",
            [ACTIVITY_TYPES.OS_UPGRADE]: "OS Updates",
            [ACTIVITY_TYPES.AUDIT]: "Compliance Check",
            [ACTIVITY_TYPES.BACKUP]: "Backup Management",
            [ACTIVITY_TYPES.INVENTORY_REPORT]: "Inventory Reports",
        };

        // Handle workflow activities
        if (activityType.startsWith(ACTIVITY_TYPES.WORKFLOW_PREFIX)) {
            return `Workflow: ${activityType.split(":")[1] || "Unknown"}`;
        }

        return displayNames[activityType] || activityType;
    }

    /**
     * Get currency label from code
     */
    private static getCurrencyLabel(currencyCode: string): string {
        const currencyLabels: {[key: string]: string} = {
            USD: "USD ($)",
            EUR: "EUR (€)",
            GBP: "GBP (£)",
            JPY: "JPY (¥)",
        };
        return currencyLabels[currencyCode] || `${currencyCode}`;
    }

    /**
     * Get backend configuration format for API calls (future use)
     */
    static getBackendConfig(config: IROIWidgetConfig): {
        hourlyRate: number;
        currencyCode: string;
        activities: {[activityType: string]: IActivityConfig};
    } {
        const backendConfig = this.convertToBackendFormat(config);
        return {
            hourlyRate: backendConfig.hourlyRate!,
            currencyCode: backendConfig.currencyCode!,
            activities: backendConfig.activities!,
        };
    }
}
